import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import type { CopyKey } from 'locales/copy-keys.gen'
import { getCurrencyConfig, getProjectCurrencySign, km_ify } from '@stokr/components-library'
import { configAuth } from 'shared/app-config'
import { getAmountBucket, getTokenBucket } from 'shared/analytics'
import { ProjectTypes } from 'constants/enums'
import { acceptedCurrencyRefs } from 'models/projectContent'
import {
  defaultInvestCurrency,
  formatInvestAmount,
  projectCurrencyRate,
  toInvestCurrencies,
  toPricesInProjectCurrency,
  type InvestCurrency,
  type InvestCurrencyRef,
} from 'models/investCurrency'
import { stoQueries } from './queries/stoQueries'
import { useStoProject } from './useStoProject'
import { useInvestEligibility } from './useEligibility'
import { useStoTrack } from './useStoTracking'
import {
  buildInvestUrl,
  getSecuritiesPrice,
  latestNavPrice,
  minimumInCurrency,
  sanitizeAmount,
  securitiesForAmount,
} from './invest-panel.utils'

/** The securities the typed amount buys; read-only. */
export interface SecuritiesOutput {
  /** Empty until an amount is typed and the price is known. */
  value: string
  /** Token symbol, shown in the field's pill. */
  symbol: string
}

export interface InvestPanelState {
  amount: string
  setAmount: (value: string) => void
  /** A fund's price is its NAV, which loads in the browser. */
  securities: SecuritiesOutput
  /** The project's currency code, or the picked payment currency's (`LBTC`). */
  currencyCode: string
  /** The payment currencies to pick from; fewer than two shows no menu. */
  currencies: InvestCurrency[]
  selectedCurrency?: InvestCurrencyRef
  setCurrency: (ref: InvestCurrencyRef) => void
  minimumLabel: string
  /** Open for investment, from the product alone; undefined until the project has loaded.
   *  The fields can be typed in while it is open, signed in or not. */
  isOpen: boolean | undefined
  /** Fail-closed: only open and signed in, fully eligible. The amount is optional: the flow asks for it. */
  canInvest: boolean
  /** Why the button is off, for its tooltip; none while it is on or the product is still loading. */
  lockedReasonKey?: CopyKey
  /** Eligibility still loading after sign-in; the button waits rather than showing a state. */
  isResolving: boolean
  onInvest: () => void
  /** The amount field lost focus: reports a new amount (bucketed). */
  onAmountCommit: () => void
  /** A press on the locked button: reports why it is locked. */
  onLockedClick: () => void
}

export function useInvestPanel(): InvestPanelState {
  const navigate = useNavigate()
  const { slug, project, projectId } = useStoProject()
  const { isOpen, isLoggedIn, isEligible, isResolving } = useInvestEligibility()
  const [amount, setAmount] = useState('')
  const [picked, setPicked] = useState<InvestCurrencyRef>()
  const trackSto = useStoTrack()
  const lastTrackedAmount = useRef('')

  // The project's own field, else the `general` content (the page's single content request).
  const { data: content } = useQuery(stoQueries.content(projectId))
  const refs = acceptedCurrencyRefs(project, content)
  const currencies = useMemo(() => toInvestCurrencies(refs), [refs])
  const tokenCurrency = project?.tokenCurrency ?? ''
  const currency = currencies.find((item) => item.ref === picked) ?? defaultInvestCurrency(currencies, tokenCurrency)

  // Another currency than the project's converts at live rates, fetched only once one is picked.
  const foreign = Boolean(currency && currency.rateKey !== tokenCurrency.toUpperCase())
  const { data: rawRates } = useQuery(stoQueries.exchangeRates(foreign))
  const prices = useMemo(
    () => (rawRates ? toPricesInProjectCurrency(rawRates, tokenCurrency) : undefined),
    [rawRates, tokenCurrency],
  )
  const rate = currency ? projectCurrencyRate(currency, tokenCurrency, prices) : 1

  const currencyCode = currency?.code ?? tokenCurrency
  // Same source as the investment flow (`currency.decimals`): the shared currency table.
  const decimals = currency?.decimals ?? getCurrencyConfig(tokenCurrency).decimals
  const minimum = Number(project?.sales?.find((sale) => sale.isVariable)?.purchaseMinimum ?? Number.NaN)
  const minimumLabel = !Number.isFinite(minimum)
    ? ''
    : !foreign
      ? `${getProjectCurrencySign(tokenCurrency)}${km_ify(minimum, 2)}`
      : rate && currency
        ? formatInvestAmount(minimumInCurrency(minimum, rate, decimals), currency)
        : ''
  // Same query as the fund's chart, so the NAV is fetched once.
  const { data: navValues } = useQuery(
    stoQueries.nav(project?.type === ProjectTypes.FUND ? project.secondaryAssetId?.trim() || undefined : undefined),
  )
  const price = getSecuritiesPrice(project, latestNavPrice(navValues))
  const securities = price && rate ? securitiesForAmount(amount, price, rate) : ''

  const numeric = Number(amount)
  const hasValidAmount = amount.trim() !== '' && Number.isFinite(numeric) && numeric > 0
  const canInvest = isOpen === true && isEligible
  const lockedReasonKey: CopyKey | undefined =
    canInvest || isOpen === undefined
      ? undefined
      : !isOpen
        ? 'stoPage.keyActions.invest.lockedReason.closed'
        : !isLoggedIn
          ? 'stoPage.keyActions.invest.lockedReason.signIn'
          : 'stoPage.keyActions.invest.lockedReason.steps'

  // Buckets only, never the amount (Notion: Events dictionary, global rules).
  const amountProps = () => {
    const inProjectCurrency = hasValidAmount && rate ? numeric * rate : undefined
    return {
      has_amount: hasValidAmount,
      currency: currencyCode,
      amount_bucket: inProjectCurrency === undefined ? undefined : getAmountBucket(inProjectCurrency),
      token_bucket: securities ? getTokenBucket(Number(securities)) : undefined,
      below_minimum:
        inProjectCurrency === undefined || !Number.isFinite(minimum) ? undefined : inProjectCurrency < minimum,
    }
  }

  const onAmountCommit = () => {
    if (!hasValidAmount || amount === lastTrackedAmount.current) return
    lastTrackedAmount.current = amount
    trackSto('sto_invest_amount_entered', amountProps())
  }

  const onLockedClick = () => {
    if (lockedReasonKey) trackSto('sto_invest_locked_clicked', { reason: lockedReasonKey.split('.').pop() })
  }

  const onInvest = () => {
    if (!canInvest || !slug) return
    trackSto('invest_button_clicked', { location: 'key_actions', ...amountProps() })
    // A typed amount rides along as the prefill; without one the flow starts empty.
    navigate(
      buildInvestUrl(
        configAuth.investUrl ?? '/invest',
        slug,
        hasValidAmount ? amount : '',
        currency?.ref ?? tokenCurrency,
      ),
    )
  }

  return {
    amount,
    setAmount: (value: string) => setAmount(sanitizeAmount(value, decimals)),
    securities: { value: securities, symbol: project?.tokenSymbol ?? '' },
    currencyCode,
    currencies,
    selectedCurrency: currency?.ref,
    setCurrency: (ref: InvestCurrencyRef) => {
      const next = currencies.find((item) => item.ref === ref)
      if (ref !== currency?.ref) trackSto('sto_invest_currency_selected', { currency: next?.code ?? ref })
      setPicked(ref)
      // The typed amount keeps no more decimals than the new currency has.
      if (next) setAmount((value) => sanitizeAmount(value, next.decimals))
    },
    minimumLabel,
    isOpen,
    canInvest,
    lockedReasonKey,
    isResolving,
    onInvest,
    onAmountCommit,
    onLockedClick,
  }
}
